import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import Navbar from '../components/Navbar';
import { Calendar, Award, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate } from '../utils/dateTime';

const MyHours = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hourRequests, setHourRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHourRequests();
  }, []);

  const fetchHourRequests = async () => {
    try {
      const response = await axios.get(`${API}/hours/my`);
      setHourRequests(response.data);
    } catch (error) {
      toast.error('Failed to load hours');
    } finally {
      setLoading(false);
    }
  };

  const pendingHours = hourRequests.filter(req => req.status === 'pending');
  const approvedHours = hourRequests.filter(req => req.status === 'approved');

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="my-hours-page">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-3">My Hours</h1>
          <div className="flex items-center gap-2 text-base text-[#666666]">
            <Award className="w-5 h-5 text-[#C9A227]" />
            <span>Total Approved Hours: <strong className="text-[#222222]" data-testid="total-hours">{user?.total_approved_hours || 0}</strong></span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-2xl font-bold text-[#222222] mb-4">Pending Approval</h2>
            {pendingHours.length === 0 ? (
              <div className="card text-center py-8" data-testid="no-pending-message">
                <p className="text-[#666666]">No pending hour requests.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingHours.map((request) => {
                  const oppDate = parseLocalDate(request.opportunity_date);
                  const formattedDate = format(oppDate, 'MMM d, yyyy');
                  
                  return (
                    <div key={request.id} className="card" data-testid={`pending-request-${request.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-bold text-[#222222]">
                          {request.opportunity_title}
                        </h3>
                        <span className="badge badge-pending">Pending</span>
                      </div>
                      <div className="flex flex-col gap-2 text-sm text-[#666666]">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formattedDate}
                        </div>
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4" />
                          {request.credit_hours} hours requested
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Requested {format(new Date(request.requested_at), 'MMM d')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#222222] mb-4">Approved Hours</h2>
            {approvedHours.length === 0 ? (
              <div className="card text-center py-8" data-testid="no-approved-message">
                <p className="text-[#666666]">No approved hours yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {approvedHours.map((request) => {
                  const oppDate = parseLocalDate(request.opportunity_date);
                  const formattedDate = format(oppDate, 'MMM d, yyyy');
                  
                  return (
                    <div key={request.id} className="card" data-testid={`approved-request-${request.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-bold text-[#222222]">
                          {request.opportunity_title}
                        </h3>
                        <div className="flex flex-col items-end gap-1">
                          <span className="badge badge-approved">Approved</span>
                          {request.manual_entry && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800" data-testid={`manual-badge-${request.id}`}>
                              Admin/Partner Added
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 text-sm text-[#666666]">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formattedDate}
                        </div>
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-[#C9A227]" />
                          <strong className="text-[#222222]">{request.credit_hours} hours</strong> approved
                        </div>
                        {request.manual_entry && request.entered_by_name && (
                          <p className="text-xs text-[#888] italic">
                            Manually credited by {request.entered_by_name}
                            {request.reason ? ` — ${request.reason}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyHours;